import type { ReactNode, RefObject } from 'react';
import { safePrecision } from '@matix/utils';
import { moneyPrecision } from '../../store/currency';
import { useLang } from '../../lib/language';
import { useLocalizedName } from '../../lib/localizedName';
import type { ReportPaper } from './reportPrint';

/**
 * MONEY cells — formatted at the tenant currency's precision (2 on EGP/SAR/AED,
 * 3 on KWD). Use this for any amount column; `fmt2` stays for plain 2-decimal
 * numbers like item counts.
 */
export function fmtMoney(n: number | null | undefined): string {
  const digits = safePrecision(moneyPrecision());
  return (n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Number formatters for report cells. */
export function fmt2(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
export function fmt3(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
export function fmtInt(n: number | null | undefined): string {
  return Math.round(n ?? 0).toLocaleString();
}
export function fmtPct(n: number | null | undefined): string {
  return `${(n ?? 0).toFixed(2)}%`;
}

/** Today as YYYY-MM-DD in local time (default for date filters). */
export function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ── Date + time filters ───────────────────────────────────────────────────
 *
 * Every report filters on a DATETIME at each end, not a date (Quick Sales
 * since 2026-09-13, the rest since 2026-09-15): a branch running past
 * midnight, or a supervisor checking the last two hours, needs the time of
 * day. The time lives INSIDE each date picker (`datetime-local`) rather than
 * in a field of its own, so the filter bar still reads "From … To …".
 *
 * The wire format is unchanged — the value is split into the `from`/`to`
 * dates the endpoints already took plus `from_time`/`to_time` — and the
 * defaults (00:00 → 23:59 today) reproduce the old whole-day range.
 */

export const DAY_START = '00:00';
export const DAY_END = '23:59';

/** `YYYY-MM-DDTHH:mm` for the start of today — the default `From`. */
export function todayFrom(): string { return `${todayStr()}T${DAY_START}`; }
/** …and the end of today — the default `To`. */
export function todayTo(): string { return `${todayStr()}T${DAY_END}`; }

/** `YYYY-MM-DDTHH:mm` → the parts the API takes; time null when absent. */
export function splitLocal(v: string): { date: string; time: string | null } {
  const [date = '', time = ''] = v.split('T');
  return { date, time: /^\d{2}:\d{2}/.test(time) ? time.slice(0, 5) : null };
}

/**
 * The `from` / `to` / `from_time` / `to_time` query parameters for a pair of
 * `datetime-local` values, or `null` when either end has no date yet. A
 * half-typed picker (a date with no time) falls back to the whole day on that
 * end rather than refusing to run.
 */
export function rangeParams(
  from: string,
  to: string,
): { from: string; to: string; from_time?: string; to_time?: string } | null {
  const f = splitLocal(from);
  const t = splitLocal(to);
  if (!f.date || !t.date) return null;
  return {
    from: f.date,
    to: t.date,
    ...(f.time ? { from_time: f.time } : {}),
    ...(t.time ? { to_time: t.time } : {}),
  };
}

/** How a datetime range reads on screen, in the printout and in the workbook. */
export function rangeLabel(from: string, to: string): string {
  const f = splitLocal(from);
  const t = splitLocal(to);
  return `${f.date}${f.time ? ` ${f.time}` : ''} → ${t.date}${t.time ? ` ${t.time}` : ''}`;
}

/** The `From` / `To` pair every report's filter bar carries. */
export function DateTimeRange({
  from, to, onFrom, onTo,
}: {
  from: string; to: string;
  onFrom: (v: string) => void; onTo: (v: string) => void;
}): JSX.Element {
  const { t } = useLang();
  return (
    <>
      <Field label={t({ en: 'From', ar: 'من' })}>
        <input type="datetime-local" step={60} value={from} onChange={(e) => onFrom(e.target.value)} />
      </Field>
      <Field label={t({ en: 'To', ar: 'إلى' })}>
        <input type="datetime-local" step={60} value={to} onChange={(e) => onTo(e.target.value)} />
      </Field>
    </>
  );
}

/**
 * Display a bilingual lookup label — branch, cashier, category, salesman.
 *
 * A hook rather than a plain function because the choice depends on the active
 * language: an Arabic till showed English category and branch names because
 * this used to be "name, or name_ar if empty" with no idea what language the
 * screen was in. Falls back to the other script when a tenant has filled in
 * only one — see lib/localizedName.
 */
export function useLabelOf(): (item: { name: string; name_ar: string | null }) => string {
  return useLocalizedName();
}

/**
 * Filter bar + run/print toolbar shared by every report. Two print buttons:
 * the till's roll printer and an A4 sheet — the same report, laid out for
 * whichever printer the cashier is standing next to.
 *
 * "Export to Excel" is RENDERED only once the report has data, rather than
 * shown disabled like the print buttons: a spreadsheet of an empty run is a
 * file nobody wants, and the button appearing is the clearest sign the run
 * returned something.
 */
export function ReportToolbar({
  filters, onRun, onExport, onExportExcel, running, canExport,
}: {
  filters: ReactNode;
  onRun: () => void;
  onExport: (paper: ReportPaper) => void;
  onExportExcel?: () => void;
  running: boolean;
  canExport: boolean;
}): JSX.Element {
  const { t } = useLang();
  return (
    <div className="rpt-toolbar">
      <div className="rpt-filters">{filters}</div>
      <div className="rpt-actions">
        <button type="button" className="rpt-btn rpt-btn-primary" onClick={onRun} disabled={running}>
          {running ? t({ en: 'Loading…', ar: 'جارٍ التحميل…' }) : t({ en: 'Show report', ar: 'عرض التقرير' })}
        </button>
        {/* Always rendered (disabled until there is data), like Print: a button
            that appears on "Show report" widened the row and wrapped the actions
            onto a new line on a full toolbar (Sales summary, 2026-10-06). */}
        {onExportExcel && <ExcelButton onClick={onExportExcel} disabled={!canExport} />}
        <button type="button" className="rpt-btn" onClick={() => onExport('roll')} disabled={!canExport}>
          {t({ en: 'Print', ar: 'طباعة' })}
        </button>
        <button type="button" className="rpt-btn" onClick={() => onExport('a4')} disabled={!canExport}>
          {t({ en: 'Print A4', ar: 'طباعة A4' })}
        </button>
      </div>
    </div>
  );
}

/** The green "Export to Excel" button, wherever a report offers one. */
export function ExcelButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }): JSX.Element {
  const { t } = useLang();
  return (
    <button type="button" className="rpt-btn rpt-btn-excel" onClick={onClick} disabled={disabled}>
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M9.5 1H3.2A1.2 1.2 0 0 0 2 2.2v11.6A1.2 1.2 0 0 0 3.2 15h9.6a1.2 1.2 0 0 0 1.2-1.2V5.5L9.5 1Zm0 1.6L12.4 5.5H9.5V2.6ZM5.6 12.4 7.1 9.9 5.6 7.4h1.5l.8 1.5.8-1.5h1.5L8.7 9.9l1.5 2.5H8.7l-.8-1.5-.8 1.5H5.6Z"
        />
      </svg>
      {t({ en: 'Export to Excel', ar: 'تصدير إلى Excel' })}
    </button>
  );
}

/** Labeled field wrapper for a filter control. */
export function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="rpt-field">
      <span className="rpt-field-label">{label}</span>
      {children}
    </label>
  );
}

/** Grab the printable region's HTML for the PDF export. */
export function printableHtml(ref: RefObject<HTMLDivElement>): string {
  return ref.current?.innerHTML ?? '';
}

/** `YYYY-MM-DDTHH:mm:ss.sss` (tenant wall clock, no zone) → `YYYY-MM-DD HH:mm`. */
export function wallClock(s: string | null): string {
  if (!s) return '';
  return s.replace('T', ' ').slice(0, 16);
}
